import { isToday, isTomorrow, isYesterday } from "date-fns";
import { CorrectionType, TestResultStatus, TestType } from "./enums";
import { v4 } from "uuid";
import api from "./api";

export function capitalize(string) {
    // Verifica se la stringa è vuota
    if (!string) {
        return "";
    }

    // Dividi la stringa in un array di parole
    const words = string.split(' ');

    // Capitalizza ogni parola nell'array
    const capitalizedWords = words.map(word => {
        // Capitalizza la prima lettera di ogni parola
        return word.charAt(0).toUpperCase() + word.slice(1);
    });

    // Unisci le parole capitalizzate in una stringa e restituiscila
    return capitalizedWords.join(' ');
}

export function filterArray(arr, str) {
    return arr.filter(item => {
        const words = str
            .toLowerCase()
            .split(' ')
            .filter(word => word.length > 2); // Dividi la stringa in parole lunghe più di 3 lettere
        return words.every(word => item.toLowerCase().includes(word)); // Controlla se tutte le parole sono incluse nella stringa
    });
}

export function sortFilteredArray(arr, str) {
    return arr.sort((a, b) => {
        const startsWithStrA = a.toLowerCase().startsWith(str.toLowerCase());
        const startsWithStrB = b.toLowerCase().startsWith(str.toLowerCase());

        // Se entrambi iniziano con la stringa specificata, li ordina normalmente
        if (startsWithStrA && startsWithStrB) {
            return a.localeCompare(b);
        }
        // Se solo uno inizia con la stringa, lo mette prima
        else if (startsWithStrA) {
            return -1;
        } else if (startsWithStrB) {
            return 1;
        }
        // Altrimenti, li ordina normalmente
        else {
            return a.localeCompare(b);
        }
    });
}

// Funzione principale
export function filterAndSortArray(arr, str) {
    const filteredArr = filterArray(arr, str);
    return sortFilteredArray(filteredArr, str);
}

export function formatPrice(num) {
    if (!num) {
        return "00,00"
    }
    // Converti il numero in una stringa e sostituisci il punto decimale con la virgola
    let formattedNum = num.toString().replace('.', ',');

    // Se il numero contiene una virgola
    if (formattedNum.includes(',')) {
        // Dividi la parte intera dalla parte decimale
        let parts = formattedNum.split(',');

        // Formatta la parte intera aggiungendo i punti come separatore ogni 3 cifre
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');

        // Unisci le due parti e restituisci il risultato
        return parts.join(',');
    } else {
        // Se non ci sono decimali, aggiungi ",00" alla fine
        formattedNum += ',00';
    }

    // Se la parte intera ha più di tre cifre, aggiungi i punti come separatore ogni 3 cifre
    if (formattedNum.length > 3) {
        formattedNum = formattedNum.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    return formattedNum;
}

export function splitArray(array, nParts) {
    var subArrays = []
    for (let i = 0; i < nParts; i++) {
        subArrays.push([])
    }
    for (let j = 0; j < array.length; j++) {
        const index = j % subArrays.length; // Calcola l'indice del colore utilizzando l'operatore modulo
        subArrays[index].push(array[j])
    }

    return subArrays
}

export function chunkArray(array, chunkSize) {
    const result = [];
    for (let i = 0; i < array.length; i += chunkSize) {
        result.push(array.slice(i, i + chunkSize));
    }
    return result;
}

export function hexAlpha(color, alpha) {
    // Converti la percentuale alpha in un valore compreso tra 0 e 1
    const _alpha = alpha / 100;

    // Calcola il valore alpha in esadecimale
    const alphaHex = Math.round(_alpha * 255).toString(16).toUpperCase();

    // Assicurati che il valore esadecimale abbia due cifre
    const alphaPadded = alphaHex.padStart(2, '0');

    // Restituisci il colore esadecimale con alpha
    return color + alphaPadded;
}

export function formatDate(dateString, compact = false, includeWeekDay = true) {
    let date = new Date(dateString)
    // Array dei nomi dei giorni della settimana
    const daysOfWeek = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];

    // Array dei nomi dei mesi
    const months = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

    // Ottieni il giorno della settimana, il giorno del mese e il mese dalla data
    const dayOfWeek = compact ? daysOfWeek[date.getDay()].substring(0, 3) : daysOfWeek[date.getDay()];
    const dayOfMonth = date.getDate();
    const month = compact ? months[date.getMonth()].substring(0, 3) : months[date.getMonth()];

    if (isToday(date)) {
        return 'Oggi'
    }
    else if (isTomorrow(date)) {
        return 'Domani'
    }
    else if (isYesterday(date)) {
        return 'Ieri'
    }

    // Formatta la data nel formato desiderato
    const formattedDate = includeWeekDay ? `${dayOfWeek} ${dayOfMonth} ${month}` : `${dayOfMonth} ${month}`

    return formattedDate;
}

export function formatDateV2(timestamp, options, lng = "it", applyTz = true, forceNumeric = false) {
    const date = new Date(timestamp)
    let userDate = date

    if (applyTz) {
        const offset = date.getTimezoneOffset()
        userDate = new Date(date.getTime() - (offset * 60 * 1000));
    }

    if (!forceNumeric) {
        if (isToday(userDate)) {
            return 'Oggi'
        }
        else if (isTomorrow(userDate)) {
            return 'Domani'
        }
        else if (isYesterday(userDate)) {
            return 'Ieri'
        }
    }

    var baseOptions = { month: 'long', day: 'numeric' };
    baseOptions = options ? options : baseOptions
    return userDate.toLocaleDateString(lng, baseOptions)
}

export function formatTimeV2(timestamp, options, lng = "it", applyTz = true) {
    try {
        if (!timestamp) {
            return "";
        }
        // Estrai solo l'ora dal timestamp e sostituisci la data con quella attuale
        const currentDate = new Date(timestamp).toISOString().split('T')[0]; // Ottieni la data attuale in formato yyyy-mm-dd
        const timePart = timestamp.split('T')[1]; // Estrai la parte dell'ora dal timestamp
        timestamp = `${currentDate}T${timePart}`; // Ricostruisci il timestamp con la data attuale

        // Convert AM/PM format to 24-hour format if necessary
        if (timestamp.includes('AM') || timestamp.includes('PM')) {
            const [datePart, timePart] = timestamp.split('T');
            const [time, period] = timePart.split(' ');
            let [hours, minutes, seconds] = time.split(':').map(Number);

            if (period === 'PM' && hours < 12) {
                hours += 12;
            } else if (period === 'AM' && hours === 12) {
                hours = 0;
            }

            // Ricostruisci il timestamp con padding corretto
            timestamp = `${datePart}T${String(hours).padStart(2, '0')}:${String(minutes || 0).padStart(2, '0')}:${String(seconds || 0).padStart(2, '0')}`;
        }

        const date = new Date(timestamp);
        if (isNaN(date.getTime())) {
            console.error(`${timestamp} generated error: Invalid Date Format`);
        }

        var userDate = date;
        if (applyTz) {
            const offset = date.getTimezoneOffset();
            userDate = new Date(date.getTime() - (offset * 60 * 1000));
        }
        var baseOptions = { hour: '2-digit', minute: '2-digit' };
        baseOptions = options ? options : baseOptions;
        return userDate.toLocaleTimeString(lng, baseOptions);
    }
    catch (e) {
        console.error(`Error in formatTimeV2 fot timestamp  ${timestamp}: `, e);
    }
}

export function formatTime(dateString) {
    let date = new Date(dateString)
    // Ottieni le ore e i minuti dalla data
    const hours = date.getHours();
    const minutes = date.getMinutes();

    // Aggiungi uno zero iniziale se i minuti sono inferiore a 10
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

    // Formatta l'ora nel formato HH:mm
    const formattedTime = `${hours}:${formattedMinutes}`;

    return formattedTime;
}

export function getMonthName(dateString, compact = false) {
    const date = new Date(dateString)
    const months = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
    return compact ? months[date.getMonth()].substring(0, 3) : months[date.getMonth()];
}

export function getDayName(dateString, compact = false) {
    const date = new Date(dateString)
    const daysOfWeek = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
    return compact ? daysOfWeek[date.getDay()].substring(0, 3) : daysOfWeek[date.getDay()];
}

export function hourDiff(dateString1, dateString2) {
    // Converti le stringhe in oggetti Date
    const date1 = new Date(dateString1);
    const date2 = new Date(dateString2);

    // Calcola la differenza in millisecondi
    const msDiff = Math.abs(date1 - date2);

    // Converti la differenza in ore
    const hDiff = Math.floor(msDiff / (1000 * 60 * 60));

    return hDiff;
}

export function calcLastUpdate(dateString, compact, tz = true) {
    let now = new Date();
    let date = new Date(dateString);

    if (tz) {
        let offset = date.getTimezoneOffset();
        date = new Date(date.getTime() - (offset * 60 * 1000));
    }

    const msDiff = Math.abs(now - date);

    const sDiff = parseInt(msDiff / (1000))
    const mDiff = parseInt(msDiff / (1000 * 60))
    const hDiff = parseInt(msDiff / (1000 * 60 * 60))
    const dDiff = parseInt(msDiff / (1000 * 60 * 60 * 24))
    const wDiff = parseInt(msDiff / (1000 * 60 * 60 * 24 * 7))

    var result = compact ? `${wDiff}s` : `${wDiff} ${wDiff === 1 ? 'settimana' : 'settimane'} fa`

    if (wDiff < 1) {
        result = compact ? `${dDiff}g` : `${dDiff} ${dDiff === 1 ? 'giorno' : 'giorni'} fa`
        if (hDiff < 24) {
            result = compact ? `${hDiff}h` : `${hDiff} ${hDiff === 1 ? 'ora' : 'ore'} fa`
            if (hDiff < 1) {
                result = compact ? `${mDiff}m` : `${mDiff} ${mDiff === 1 ? 'minuto' : 'minuti'} fa`
                if (mDiff < 1) {
                    result = compact ? `${sDiff}sec` : `${sDiff} ${sDiff === 1 ? 'secondo' : 'secondi'} fa`
                }
            }
        }
    }

    return result;
}

/**
 * This function removes the duplicates from an array checking the id field.
 * @param {*} array 
 * @returns the array with no duplicates objects.
 */
export const dropDuplicates = (array) => {
    const uniqueObjects = {};
    const result = [];

    // Itera sull'array originale
    array.forEach(obj => {
        // Verifica se l'oggetto con lo stesso id è già presente nell'oggetto uniqueObjects
        if (!uniqueObjects[obj.id]) {
            // Se non è presente, aggiungi l'oggetto all'oggetto uniqueObjects e al risultato
            uniqueObjects[obj.id] = true;
            result.push(obj);
        }
    });

    return result;
};

export function formatFileSize(byteSize) {
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    let size = byteSize;
    let i = 0;

    while (size >= 1000 && i < units.length - 1) {
        size /= 1000;
        i++;
    }

    return size.toFixed(2) + ' ' + units[i];
}

export function getLocaleISOString() {
    const currentDate = new Date().toLocaleDateString()
    const currentTime = new Date().toLocaleTimeString()
    const components = currentDate.split('/')
    const isoString = `${components[2]}-${components[1].padStart(2, '0')}-${components[0].padStart(2, '0')}T${currentTime}`
    return isoString
}

export function toLetter(value) {
    return (value + 9).toString(36)
}

export function isTestValid(name, module, lesson, innerTests) {
    let areInnerTestValid = true
    const isModuleValid = module !== null && module.id !== null && module.id > 0
    const isLessonValid = true

    for (const innerTest of innerTests) {
        if (innerTest.isValid === false) {
            areInnerTestValid = false
            break;
        }
    }

    return {
        isValid: name !== null && name !== "" && areInnerTestValid && isModuleValid && isLessonValid,
        isNameEmpty: !name,
        areInnerTestValid,
        isModuleValid,
        isLessonValid
    }
}

export function isTestChoiceValid(text, answers) {
    let atLeastOneIsCorrect = false
    let atLeastOneIsIncorrect = false
    let isAnswerTextValid = true

    for (const a of answers) {
        if (a.isCorrect) {
            atLeastOneIsCorrect = true
        } else if (!a.isCorrect) {
            atLeastOneIsIncorrect = true
        }

        if (!a.text) {
            isAnswerTextValid = false
        }
    }

    return {
        isValid: text !== "" && answers.length > 0 && atLeastOneIsCorrect && atLeastOneIsIncorrect && isAnswerTextValid,
        noQuestion: text === "",
        noAnswers: !answers || answers.length === 0,
        atLeastOneIsCorrect,
        atLeastOneIsIncorrect,
        isAnswerTextValid
    }
}

export function isTestTrueFalseValid(answers) {
    let everyAnswerHasSolution = true
    let isAnswerTextValid = true

    for (const a of answers) {
        if (!a.text) {
            isAnswerTextValid = false
        }

        if (a.value === null || a.value === undefined) {
            everyAnswerHasSolution = false
        }
    }

    return {
        isValid: everyAnswerHasSolution && answers.length > 0 && isAnswerTextValid,
        everyAnswerHasSolution,
        isAnswerTextValid
    }
}

export function isTestTextCompletionValid(words) {
    let atLeastOneHiddenWord = false

    for (const w of words) {
        if (w.hidden === true) {
            atLeastOneHiddenWord = true
        }
    }

    return {
        isValid: words.length > 0 && atLeastOneHiddenWord,
        atLeastOneHiddenWord,
        emptyText: words.length === 0
    }
}


export function getDefaultTest() {
    return {
        name: "",
        description: "",
        module: null,
        lesson: null,
        tags: [],
        can_be_retried: false,
        expires_at: null,
        expires_in: null,
        correction_type: CorrectionType.Solutions,
        success_threshold: 1,
        content: {
            innerTests: [
                {
                    testType: TestType.SingleChoice,
                    text: "",
                    tags: [],
                    answers: [{
                        id: v4(),
                        text: "",
                        isCorrect: false
                    }, {
                        id: v4(),
                        text: "",
                        isCorrect: false
                    }]
                }
            ]
        }
    }
}


export function isTestCompleted(innerTests) {
    let completed = 0
    for (const it of innerTests) {
        for (const r of it.responses) {
            if (r.value) {
                completed += 1
                break
            }
        }
    }
    if (completed === innerTests.length) {
        return TestResultStatus.Passed
    } else if (completed > 0 && completed < innerTests.length) {
        return TestResultStatus.Partial
    } else {
        return TestResultStatus.NotCompleted
    }
}

export async function checkImage(url) {
    const res = await fetch(url, { mode: "no-cors" });
    const buff = await res.blob();
    return buff.type.startsWith('image/')

}


export function secondsToHours(seconds, compact = false) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    let s = `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`;
    if (!compact) {
        s += ` ${String(Math.round(remainingSeconds)).padStart(2, '0')}s`
    }
    return s;
}

export function getSingleWords(text) {
    return text.split(/[\s\n]+/)
        .filter(w => w !== " " && w !== "")
        .map(s => {
            const markdown = s.slice()
            const clean = s.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]|<\/?u>|x20;/g, '')
            return {
                text: clean,
                markdown,
                hidden: false,
            }
        })
}

export function wrapWordsWithAsterisks(text) {
    // Regex per trovare il testo tra * o ** asterischi
    return text.replace(/(\*+)(.*?)\1/g, (match, asterisks, content) => {
        // Wrappare ogni parola con lo stesso numero di asterischi
        const wrappedWords = content.split(' ').map(word => `${asterisks}${word}${asterisks}`).join(' ');
        return wrappedWords;
    });
}

export function getLocalDate(noTimezoneDate) {
    let date = new Date(noTimezoneDate)
    const offset = date.getTimezoneOffset()
    return new Date(date.getTime() - (offset * 60 * 1000))
}

export const computeChoiceResult = (innerTest, user_test) => {
    let correct = 0
    let wrong = 0
    let notAnswered = 0

    const correctAnswers = innerTest.answers.filter(a => a.isCorrect).map(a => a.id)
    const wrongAnswers = innerTest.answers.filter(a => !a.isCorrect).map(a => a.id)
    const userAnswers = user_test.content.find(c => c.id === innerTest.id).responses.filter(r => r.value).map(r => r.id)

    for (const ua of userAnswers) {
        if (correctAnswers.includes(ua)) {
            correct += 1
        } else if (wrongAnswers.includes(ua)) {
            wrong += 1
        }
    }

    if (correctAnswers.length > userAnswers.length) {
        notAnswered += correctAnswers.length - userAnswers.length
    }
    return { correct, wrong, notAnswered };
}

export const computeTrueFalseResult = (innerTest, user_test) => {
    let correct = 0
    let wrong = 0
    let notAnswered = 0
    const { answers } = innerTest

    const trueAnswers = answers.filter(a => a.value).map(a => a.id)
    const falseAnswers = answers.filter(a => !a.value).map(a => a.id)
    const userAnswers = user_test.content.find(c => c.id === innerTest.id).responses
    for (const ua of userAnswers) {
        if (ua.value === true) {
            if (trueAnswers.includes(ua.id)) {
                correct += 1
            } else {
                wrong += 1
            }
        } else if (ua.value === false) {
            if (falseAnswers.includes(ua.id)) {
                correct += 1
            } else {
                wrong += 1
            }
        } else if (ua.value === null) {
            notAnswered += 1
        }
    }
    return { correct, wrong, notAnswered }

}

export const computeTextCompletionResult = (innerTest, user_test) => {
    let correct = 0
    let wrong = 0
    let notAnswered = 0
    const { words } = innerTest
    for (let i = 0; i < words.length; i++) {
        if (words[i].hidden) {
            const { solutions } = words[i]
            const userAnswer = user_test.content
                .find(c => (
                    c.id === innerTest.id
                ))?.responses.find(r => (
                    r.index === i
                ))
            if (userAnswer && !userAnswer.value) {
                notAnswered += 1
            } else {
                const isCorrect = solutions.includes(userAnswer?.value)
                if (isCorrect) {
                    correct += 1
                } else {
                    wrong += 1
                }
            }
        }
    }
    return { correct, wrong, notAnswered }
}

export const formatSeconds = (seconds, text = false) => {
    if (!text) {
        if (!seconds) return '00:00';

        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);

        if (hours > 0) {
            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
        } else {
            return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
        }
    }
    else {
        if (seconds <= 0) {
            return '0s'
        }

        const secs = seconds % 60;
        const totalMinutes = Math.floor(seconds / 60);
        const mins = totalMinutes % 60;
        const totalHours = Math.floor(totalMinutes / 60);
        const hrs = totalHours % 24;
        const days = Math.floor(totalHours / 24);

        if (days > 0) {
            return `${days}g ${hrs}h ${mins}m ${secs}s`;
        } else if (hrs > 0) {
            return `${hrs}h ${mins}m ${secs}s`;
        } else if (mins > 0) {
            return `${mins}m ${secs}s`;
        } else {
            return `${secs}s`;
        }
    }
};

export function UTCtoLocal(dateString) {
    let date = new Date(dateString)
    const offset = date.getTimezoneOffset()
    return new Date(date.getTime() - (offset * 60 * 1000))
}

const uploadChunk = async ({ url, chunk, partNumber, onProgress }) => {
    try {
        const response = await fetch(url, {
            method: 'PUT',
            body: chunk,
        });

        if (!response.ok) {
            throw new Error(`Upload failed with status ${response.status}`);
        }

        // La risposta di un PUT verso S3/Spaces non ha un corpo JSON, ma l’ETag è nei headers
        const etag = response.headers.get('Etag')?.replaceAll('"', '');
        if (!etag) throw new Error('ETag not found');

        // `fetch` non supporta `onUploadProgress` nativamente, ma possiamo simulare il progresso solo per chunk interi
        onProgress?.(100, partNumber);

        return { PartNumber: partNumber, ETag: etag };
    } catch (error) {
        console.error(`Error uploading chunk ${partNumber}:`, error);
        throw new Error(`Failed to upload chunk ${partNumber}`);
    }
};

export const uploadMultipart = async (file, subdirectory, add_timestamp = true, onGlobalProgress) => {
    const chunkSize = 5 * 1024 * 1024;
    const totalChunks = Math.ceil(file.size / chunkSize);
    const parts = [];

    let initData = {
        filename: file.name,
    }

    if (subdirectory) {
        initData.subdirectory = subdirectory;
    }

    const { uploadId, key } = await api.post(`/teacher/upload/initiate?add_timestamp=${add_timestamp}`, initData);

    let uploadedTotal = 0;

    for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(file.size, start + chunkSize);
        const chunk = file.slice(start, end);
        const partNumber = i + 1;
        const baseUploaded = uploadedTotal;

        const { url } = await api.get(`/teacher/upload/presigned-url`, {
            params: {
                upload_id: uploadId,
                key: key,
                part_number: partNumber
            }
        });

        const part = await uploadChunk({
            url,
            chunk,
            partNumber,
            onProgress: (percent) => {
                // Calcolo progresso del singolo chunk in byte
                const currentUploaded = (percent / 100) * chunk.size;
                const globalProgress = Math.min(
                    Math.round(((baseUploaded + currentUploaded) / file.size) * 100),
                    100
                );
                onGlobalProgress?.(globalProgress);
            }
        });

        // Una volta finito il chunk, aggiungilo ai bytes totali caricati
        uploadedTotal = baseUploaded + chunk.size;

        parts.push(part);
    }

    const { url: finalUrl } = await api.post('/teacher/upload/complete', {
        uploadId,
        key,
        parts,
    });

    return { url: finalUrl };
};

const resolveTimeZone = (explicitTimeZone) => {
    if (explicitTimeZone) {
        return explicitTimeZone;
    }
    if (typeof Intl !== "undefined" && typeof Intl.DateTimeFormat === "function") {
        try {
            const { timeZone } = new Intl.DateTimeFormat().resolvedOptions();
            if (timeZone) {
                return timeZone;
            }
        }
        catch (error) {
            // Fall back to default when timezone cannot be resolved.
        }
    }
    return "UTC";
};

export function groupMessagesByDate(messages, options = {}) {
    if (!Array.isArray(messages) || messages.length === 0) {
        return {};
    }

    const { timeZone: explicitTimeZone, reverse = false } = options;
    const timeZone = resolveTimeZone(explicitTimeZone);
    let formatter = null;

    try {
        formatter = new Intl.DateTimeFormat("en-CA", {
            timeZone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        });
    }
    catch (error) {
        // Formatter is not available for the provided timezone; fall back to manual formatting.
    }

    const fallbackFormat = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const normalizedMessages = messages.reduce((acc, message) => {
        if (!message || !message.created_at) {
            return acc;
        }
        const parsedDate = new Date(message.created_at);
        if (Number.isNaN(parsedDate.getTime())) {
            return acc;
        }
        acc.push({ message, date: parsedDate });
        return acc;
    }, []);

    normalizedMessages.sort((a, b) => a.date - b.date);

    const grouped = normalizedMessages.reduce((groups, { message, date }) => {
        let formattedDate = null;
        if (formatter) {
            formattedDate = formatter.format(date);
        } else if (timeZone && typeof Intl !== "undefined" && typeof Intl.DateTimeFormat === "function") {
            try {
                const zonedDate = new Date(date.toLocaleString("en-US", { timeZone }));
                formattedDate = fallbackFormat(zonedDate);
            }
            catch (error) {
                formattedDate = fallbackFormat(date);
            }
        } else {
            formattedDate = fallbackFormat(date);
        }

        const key = `${formattedDate}T00:00:00`;
        if (!groups[key]) {
            groups[key] = [];
        }
        groups[key].push(message);
        return groups;
    }, {});

    if (!reverse) {
        return grouped;
    }

    const reversedGroups = {};
    const keys = Object.keys(grouped);
    for (let i = keys.length - 1; i >= 0; i -= 1) {
        const key = keys[i];
        reversedGroups[key] = grouped[key];
    }
    return reversedGroups;
}